import React from "react";
import { useSavedProperties } from "../../../hooks/saved/useSavedProperties";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../../contexts/TenantContext";
import { savedPropertyService } from "../../../api/savedProperties";
import { SavedProperty } from "../../../types/savedProperty";
import { Property } from "../../../types/search";
import { getPropertyDetails } from "../../../api/mls";
import { Loader, Home } from "lucide-react";
import PropertyCard from "../../features/search/PropertyCard";
import { logger } from "../../../utils/logger";

export const SavedPropertiesGrid = () => {
  const { config } = useTenant();
  const navigate = useNavigate();
  const { data: savedProperties = [], isLoading } = useSavedProperties();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader
          className="w-8 h-8 animate-spin"
          style={{ color: config?.branding?.colors?.primary }}
        />
      </div>
    );
  }

  if (!savedProperties || savedProperties.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
            <Home className="w-7 h-7 text-gray-400" />
          </div>
        </div>
        <h3 className="text-lg font-semibold mb-1 text-gray-700">
          No saved properties yet
        </h3>
        <p className="mb-6 text-gray-500">
          Start browsing to find and save your dream properties.
        </p>
        <button
          onClick={() => navigate("/search")}
          className="border-gray-500 gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray rounded-lg hover:bg-gray-600 transition-colors !bg-transparent"
        >
          Browse Properties
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {savedProperties.map((savedProperty) => {
        const property: Property = {
          id: savedProperty.propertyId || "",
          mlsNumber: savedProperty.mlsNumber || "",
          mlsBoardCode: savedProperty.mlsBoardCode,
          title: `Property in ${savedProperty.address.city}${
            savedProperty.address.state
              ? `, ${savedProperty.address.state}`
              : ""
          }`,
          propertyType: savedProperty.features?.[0] || "Property",
          price: savedProperty.price || 0,
          location: `${savedProperty.address.city}${
            savedProperty.address.state
              ? `, ${savedProperty.address.state}`
              : ""
          }`,
          address: {
            full: [
              savedProperty.address.full,
              savedProperty.address.city,
              savedProperty.address.state,
              savedProperty.address.zip,
            ]
              .filter(Boolean)
              .join(", "),
            city: savedProperty.address.city,
            state: savedProperty.address.state,
            zipcode: savedProperty.address.zip,
          },
          details: {
            bedrooms: savedProperty.bedrooms || 0,
            bathrooms: savedProperty.bathrooms || 0,
            squareFeet: savedProperty.squareFeet || 0,
          },
          bedrooms: savedProperty.bedrooms || 0,
          bathrooms: savedProperty.bathrooms || 0,
          squareFeet: savedProperty.squareFeet || 0,
          listprice: savedProperty.listprice,
          listpricelow: savedProperty.listpricelow,
          internetAddressDisplayYN: savedProperty.internetAddressDisplayYN,
          displayRules: {
            mustRemovePrimaryPhoto: savedProperty.mustRemovePrimaryPhoto,
          },
          images: savedProperty.primaryPhotoUrl
            ? [savedProperty.primaryPhotoUrl]
            : [],
          photos: savedProperty.primaryPhotoUrl
            ? [savedProperty.primaryPhotoUrl]
            : [],
          features: (savedProperty.features || []).filter(
            (feature) =>
              feature &&
              !feature.includes("null") &&
              feature !== "0 beds" &&
              feature !== "0 baths" &&
              feature !== "0 sq ft"
          ),
          listingStatus: savedProperty.listingStatus || "Active",
          mlsstatus: savedProperty.mlsstatus,
          listingOffice: {
            name: savedProperty.listingOffice || "N/A",
            email: "",
            phone: "",
            id: "",
            key: "",
          },
        };

        return (
          <PropertyCard
            key={savedProperty.id}
            property={property}
            onClick={(p) => {
              const tenantId = config?.id || "";
              const mlsOsn = p.mlsBoardCode || "";
              window.open(
                `/property/${p.id}?tenant_id=${tenantId}&mls_osn=${mlsOsn}`,
                "_blank"
              );
            }}

            //>>>> TEMP ORIG: onClick={(p) => window.open(`/property/${p.id}`, "_blank")}
          />
        );
      })}
    </div>
  );
};

export default SavedPropertiesGrid;
